import { Show, createEffect, createSignal, onCleanup, type JSX } from "solid-js";

import { uploadFile, type UploadContext } from "~/core/api/uploads";
import { Button } from "./button";
import { FormField } from "./field";
import { toast } from "./toast";

/**
 * Upload an image to storage (presign → PUT) and surface the resulting URL.
 * Drop-in replacement for the plain "paste a URL" text fields the desk used for
 * proof receipts and market logos. Keeps `value` as the canonical URL string.
 *
 * The preview of a file just uploaded comes from the local file, not from
 * `value`: a payout proof is stored privately, so its canonical URL cannot be
 * loaded by the browser. An already-saved public image (a market logo) still
 * previews from `value`.
 */
export function ImageUpload(props: {
  value: string;
  onChange: (url: string) => void;
  context: UploadContext;
  label?: string;
  hint?: string;
  accept?: string;
}): JSX.Element {
  const [busy, setBusy] = createSignal(false);
  const [localPreview, setLocalPreview] = createSignal<string | null>(null);
  const [broken, setBroken] = createSignal(false);
  let inputRef: HTMLInputElement | undefined;

  const dropLocalPreview = (): void => {
    const url = localPreview();
    if (url) URL.revokeObjectURL(url);
    setLocalPreview(null);
  };
  onCleanup(dropLocalPreview);
  // The value changing to something we did not just upload (cleared, or a
  // different record opened) makes the local preview stale.
  let uploadedValue = "";
  createEffect(() => {
    const value = props.value;
    setBroken(false);
    if (value !== uploadedValue) dropLocalPreview();
  });
  const previewSrc = (): string => localPreview() ?? props.value;

  const onFile = async (e: Event & { currentTarget: HTMLInputElement }): Promise<void> => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file, props.context);
      dropLocalPreview();
      uploadedValue = url;
      setLocalPreview(URL.createObjectURL(file));
      props.onChange(url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef) inputRef.value = "";
    }
  };

  return (
    <FormField label={props.label ?? "Image"} hint={props.hint}>
      <div class="flex flex-col gap-150">
        <Show when={props.value && !broken()}>
          <img
            src={previewSrc()}
            onError={() => setBroken(true)}
            alt="Uploaded preview"
            class="max-h-40 w-fit rounded-medium border border-[color:var(--c9-border)] bg-[var(--c9-surface-sunken)] object-contain"
          />
        </Show>
        <Show when={props.value && broken()}>
          <p class="text-body-s text-[color:var(--c9-text-subtle)]">Image saved. A preview isn't available for this file.</p>
        </Show>
        <div class="flex items-center gap-150">
          <Button appearance="default" loading={busy()} onClick={() => inputRef?.click()}>
            {props.value ? "Replace image" : "Upload image"}
          </Button>
          <Show when={props.value && !busy()}>
            <button
              type="button"
              class="text-body-s text-[color:var(--c9-text-subtle)] hover:text-[color:var(--c9-text)] hover:underline"
              onClick={() => props.onChange("")}
            >
              Remove
            </button>
          </Show>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={props.accept ?? "image/*"}
          class="hidden"
          onChange={onFile}
        />
      </div>
    </FormField>
  );
}
